import React, { useEffect, useMemo, useState } from 'react';
import { CustomerLite } from '../Plants/CreatePlant';
import { filterSearchableList } from './assetSummaryDetailsHelpers';
import { ExtractedPlant, ExtractedSite } from './summarySitePlantUtils';
import {
  filterCustomersForSearch,
  filterPlantsForSummary,
  filterSitesForSummary,
} from './summaryRowFilterUtils';

const DropdownArrow = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5"
      stroke="#FCFCFD"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type DropdownOption = {
  key: string;
  value: string;
  label: string;
};

type SummarySearchableDropdownProps = {
  isOpen: boolean;
  search: string;
  selectedId: string;
  selectedLabel: string;
  searchPlaceholder: string;
  emptyPlaceholder: string;
  allOptionLabel: string;
  options: DropdownOption[];
  inputClassName?: string;
  arrowClassName?: string;
  containerInputStyle?: React.CSSProperties;
  onToggle: () => void;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onSelectOption: (value: string) => void;
  emptyMessage?: string;
};

const searchableInputValue = (
  isOpen: boolean,
  search: string,
  selectedId: string,
  selectedLabel: string
): string => (isOpen ? search : selectedId ? selectedLabel : '');

const searchableInputPlaceholder = (
  isOpen: boolean,
  searchPlaceholder: string,
  selectedId: string,
  selectedLabel: string,
  emptyPlaceholder: string
): string => (isOpen ? searchPlaceholder : selectedId ? selectedLabel : emptyPlaceholder);

const openDropdownOnArrowKey = (e: React.KeyboardEvent, open: () => void) => {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') open();
};

const SummarySearchableDropdown: React.FC<SummarySearchableDropdownProps> = ({
  isOpen,
  search,
  selectedId,
  selectedLabel,
  searchPlaceholder,
  emptyPlaceholder,
  allOptionLabel,
  options,
  inputClassName = 'summary-top-input',
  arrowClassName = 'dropdown-arrow-topinput',
  containerInputStyle,
  onToggle,
  onSearchChange,
  onSelectAll,
  onSelectOption,
  emptyMessage,
}) => (
  <div className="searchable-select-container-summary">
    <div
      className="searchable-select-input-summary"
      style={containerInputStyle}
      onClick={onToggle}
    >
      <input
        type="text"
        value={searchableInputValue(isOpen, search, selectedId, selectedLabel)}
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={() => undefined}
        onKeyDown={(e) => openDropdownOnArrowKey(e, onToggle)}
        placeholder={searchableInputPlaceholder(
          isOpen,
          searchPlaceholder,
          selectedId,
          selectedLabel,
          emptyPlaceholder
        )}
        className={inputClassName}
        readOnly={!isOpen}
      />
      <span className={`${arrowClassName} ${isOpen ? 'open' : ''}`}>
        <DropdownArrow />
      </span>
    </div>
    {isOpen && (
      <div className="searchable-dropdown">
        <div
          className={`dropdown-option ${selectedId === '' ? 'selected' : ''}`}
          onClick={onSelectAll}
        >
          {allOptionLabel}
        </div>
        {options.map((option) => (
          <div
            key={option.key}
            className={`dropdown-option ${option.value === selectedId ? 'selected' : ''}`}
            onClick={() => onSelectOption(option.value)}
          >
            {option.label}
          </div>
        ))}
        {emptyMessage && options.length === 0 && (
          <div className="dropdown-option disabled">{emptyMessage}</div>
        )}
      </div>
    )}
  </div>
);

type DropdownKind = 'customer' | 'site' | 'plant' | 'assetType';

export const useSummaryDropdownOpenState = () => {
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isSiteOpen, setIsSiteOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [isPlantOpen, setIsPlantOpen] = useState(false);
  const [plantSearch, setPlantSearch] = useState('');
  const [isAssetTypeOpen, setIsAssetTypeOpen] = useState(false);
  const [assetTypeSearch, setAssetTypeSearch] = useState('');

  const closeOtherDropdowns = (except?: DropdownKind) => {
    if (except !== 'customer') setIsCustomerOpen(false);
    if (except !== 'site') setIsSiteOpen(false);
    if (except !== 'plant') setIsPlantOpen(false);
    if (except !== 'assetType') setIsAssetTypeOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest('.searchable-select-container-summary')) {
        closeOtherDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (kind: DropdownKind, isOpen: boolean, setOpen: (open: boolean) => void) => {
    setOpen(!isOpen);
    closeOtherDropdowns(kind);
  };

  return {
    isCustomerOpen,
    setIsCustomerOpen,
    customerSearch,
    setCustomerSearch,
    isSiteOpen,
    setIsSiteOpen,
    siteSearch,
    setSiteSearch,
    isPlantOpen,
    setIsPlantOpen,
    plantSearch,
    setPlantSearch,
    isAssetTypeOpen,
    setIsAssetTypeOpen,
    assetTypeSearch,
    setAssetTypeSearch,
    toggleDropdown,
  };
};

export type SummaryDropdownOpenState = ReturnType<typeof useSummaryDropdownOpenState>;

export type SummaryFilterDropdownsProps = {
  customers: CustomerLite[];
  extractedSites: ExtractedSite[];
  extractedPlants: ExtractedPlant[];
  selectedCustomerId: string;
  selectedSiteId: string;
  selectedPlantId: string;
  onSelectCustomer: (customerId: string) => void;
  onSelectSite: (siteId: string) => void;
  onSelectPlant: (plantId: string) => void;
  onResetPage: () => void;
  dropdownState: SummaryDropdownOpenState;
};

export const SummaryTopFilterDropdowns: React.FC<SummaryFilterDropdownsProps> = (props) => {
  const {
    customers,
    extractedSites,
    extractedPlants,
    selectedCustomerId,
    selectedSiteId,
    selectedPlantId,
    onSelectCustomer,
    onSelectSite,
    onSelectPlant,
    onResetPage,
    dropdownState,
  } = props;

  const {
    isCustomerOpen,
    setIsCustomerOpen,
    customerSearch,
    setCustomerSearch,
    isSiteOpen,
    setIsSiteOpen,
    siteSearch,
    setSiteSearch,
    isPlantOpen,
    setIsPlantOpen,
    plantSearch,
    setPlantSearch,
    toggleDropdown,
  } = dropdownState;

  const filteredCustomersList = useMemo(
    () => filterCustomersForSearch(customers, customerSearch),
    [customers, customerSearch]
  );
  const filteredSitesList = useMemo(
    () => filterSitesForSummary(extractedSites, selectedCustomerId, siteSearch),
    [extractedSites, selectedCustomerId, siteSearch]
  );
  const filteredPlantsList = useMemo(
    () => filterPlantsForSummary(extractedPlants, selectedSiteId, selectedCustomerId, plantSearch),
    [extractedPlants, selectedSiteId, selectedCustomerId, plantSearch]
  );

  const selectedCustomerLabel =
    customers.find((c) => c.customerId === selectedCustomerId)?.customerName || '';
  const selectedSiteLabel = extractedSites.find((s) => s.siteId === selectedSiteId)?.site || '';
  const selectedPlantLabel =
    extractedPlants.find((p) => p.plantId === selectedPlantId)?.plantName || '';

  const customerOptions = useMemo(
    () =>
      filteredCustomersList.map((customer, index) => ({
        key: customer.id || `${customer.customerId}-${index}`,
        value: customer.customerId,
        label: `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`,
      })),
    [filteredCustomersList]
  );

  const siteOptions = useMemo(
    () =>
      filteredSitesList.map((site, index) => ({
        key: `${site.siteId}-${index}`,
        value: site.siteId,
        label: site.site,
      })),
    [filteredSitesList]
  );

  const plantOptions = useMemo(
    () =>
      filteredPlantsList.map((plant, index) => ({
        key: `${plant.plantId}-${index}`,
        value: plant.plantId,
        label: plant.plantName,
      })),
    [filteredPlantsList]
  );

  const closeCustomer = () => {
    setIsCustomerOpen(false);
    setCustomerSearch('');
  };
  const closeSite = () => {
    setIsSiteOpen(false);
    setSiteSearch('');
  };
  const closePlant = () => {
    setIsPlantOpen(false);
    setPlantSearch('');
  };

  return (
    <div className="new-container">
      <SummarySearchableDropdown
        isOpen={isCustomerOpen}
        search={customerSearch}
        selectedId={selectedCustomerId}
        selectedLabel={selectedCustomerLabel}
        searchPlaceholder="Search customers..."
        emptyPlaceholder="All Customers"
        allOptionLabel="All Customers"
        options={customerOptions}
        containerInputStyle={{ backgroundColor: 'grey/500' }}
        inputClassName=" summary-top-input"
        onToggle={() => toggleDropdown('customer', isCustomerOpen, setIsCustomerOpen)}
        onSearchChange={setCustomerSearch}
        onSelectAll={() => {
          onSelectCustomer('');
          closeCustomer();
          onResetPage();
        }}
        onSelectOption={(customerId) => {
          onSelectCustomer(customerId);
          closeCustomer();
          onResetPage();
        }}
      />
      <span className="summary-separator-summary">\</span>
      <SummarySearchableDropdown
        isOpen={isSiteOpen}
        search={siteSearch}
        selectedId={selectedSiteId}
        selectedLabel={selectedSiteLabel}
        searchPlaceholder="Search sites..."
        emptyPlaceholder="All Sites"
        allOptionLabel="All Sites"
        options={siteOptions}
        emptyMessage="No sites found"
        onToggle={() => toggleDropdown('site', isSiteOpen, setIsSiteOpen)}
        onSearchChange={setSiteSearch}
        onSelectAll={() => {
          onSelectSite('');
          closeSite();
          onResetPage();
        }}
        onSelectOption={(siteId) => {
          onSelectSite(siteId);
          closeSite();
          onResetPage();
        }}
      />
      <span className="summary-separator-summary">\</span>
      <SummarySearchableDropdown
        isOpen={isPlantOpen}
        search={plantSearch}
        selectedId={selectedPlantId}
        selectedLabel={selectedPlantLabel}
        searchPlaceholder="Search Plants..."
        emptyPlaceholder="All Plants"
        allOptionLabel="All Plants"
        options={plantOptions}
        emptyMessage="No Plants Found"
        onToggle={() => toggleDropdown('plant', isPlantOpen, setIsPlantOpen)}
        onSearchChange={setPlantSearch}
        onSelectAll={() => {
          onSelectPlant('');
          closePlant();
          onResetPage();
        }}
        onSelectOption={(plantId) => {
          onSelectPlant(plantId);
          closePlant();
          onResetPage();
        }}
      />
    </div>
  );
};

export type SummaryAssetTypeDropdownProps = {
  assetTypes: Array<{ typeName?: string }>;
  selectedAssetType: string;
  onSelectAssetType: (typeName: string) => void;
  onResetPage: () => void;
  dropdownState: SummaryDropdownOpenState;
};

export const SummaryAssetTypeDropdown: React.FC<SummaryAssetTypeDropdownProps> = ({
  assetTypes,
  selectedAssetType,
  onSelectAssetType,
  onResetPage,
  dropdownState,
}) => {
  const {
    isAssetTypeOpen,
    setIsAssetTypeOpen,
    assetTypeSearch,
    setAssetTypeSearch,
    toggleDropdown,
  } = dropdownState;

  const filteredAssetTypesList = useMemo(
    () => filterSearchableList(assetTypes || [], assetTypeSearch, (t) => [t.typeName]),
    [assetTypes, assetTypeSearch]
  );

  const assetTypeOptions = useMemo(
    () =>
      filteredAssetTypesList.map((type, index) => ({
        key: `${type.typeName}-${index}`,
        value: type.typeName || '',
        label: type.typeName || '',
      })),
    [filteredAssetTypesList]
  );

  const closeAssetType = () => {
    setIsAssetTypeOpen(false);
    setAssetTypeSearch('');
  };

  return (
    <SummarySearchableDropdown
      isOpen={isAssetTypeOpen}
      search={assetTypeSearch}
      selectedId={selectedAssetType}
      selectedLabel={selectedAssetType}
      searchPlaceholder="Search Asset Types..."
      emptyPlaceholder="Asset Type - All"
      allOptionLabel="Asset Type - All"
      options={assetTypeOptions}
      inputClassName="summary-select searchable-input placeholder-white"
      arrowClassName="dropdown-arrow-Asset"
      onToggle={() => toggleDropdown('assetType', isAssetTypeOpen, setIsAssetTypeOpen)}
      onSearchChange={setAssetTypeSearch}
      onSelectAll={() => {
        onSelectAssetType('');
        closeAssetType();
        onResetPage();
      }}
      onSelectOption={(typeName) => {
        onSelectAssetType(typeName);
        closeAssetType();
        onResetPage();
      }}
    />
  );
};
